/**
 * Void nota persis Delphi (gelombang 8) — daftar transaksi ber-Void + registry token otorisasi void.
 *
 * Delphi TFormMGTrans.ActionVoidExecute (Forms/Template/MGTransForm.pas:1221-1294): tombol Void aktif tanpa cek hak
 * (MG_MX SettingModeShow 0). Saat diklik: boleh langsung bila hak Void menu AND (tanggal nota = hari ini OR hak
 * "Ijinkan Void Transaksi bukan hari ini" di Global Information); selain itu Log In user lain yang memenuhi aturan yang
 * sama; gagal / batal → 'Maaf, anda tidak punya otoritas untuk membatalkan .'. Pasangan BE:
 * mgmx_be/src/services/voidNota.service.js (kunci `jenis` HARUS sama dengan JENIS_VOID di sana).
 *
 * Alur web: Void diklik → hooks/useGerbangVoid memanggil GET /auth/cek-void/<jenis>/<id>; bila perlu, dialog Log In
 * (OtorisasiModal) → token otorisasi disimpan di registry ini → interceptor axios (lib/axios.ts) menempelkan header
 * `X-Otorisasi-Void` ke request void nota itu → BE memeriksa ulang saat void.
 */

/** Jenis transaksi ber-Void → path API (relatif baseURL `/api`); request void = `<base>/<id>/void`. */
export const VOID_API = {
    'po-beli': { base: '/transaksi/po-beli' },
    pembelian: { base: '/transaksi/pembelian' },
    'retur-beli': { base: '/transaksi/retur-beli' },
    'bayar-hut': { base: '/transaksi/bayar-hut' },
    'um-beli': { base: '/transaksi/um-beli' },
    'koreksi-hut-cn': { base: '/transaksi/koreksi-hut-cn' },
    'koreksi-hut-dn': { base: '/transaksi/koreksi-hut-dn' },
    jual: { base: '/transaksi/jual' },
    'so-jual': { base: '/transaksi/so-jual' },
    'retur-jual': { base: '/transaksi/retur-jual' },
    'bayar-piut': { base: '/transaksi/bayar-piut' },
    'um-jual': { base: '/transaksi/um-jual' },
    'koreksi-piut-cn': { base: '/transaksi/koreksi-piut-cn' },
    'koreksi-piut-dn': { base: '/transaksi/koreksi-piut-dn' },
    'pakai-dp': { base: '/transaksi/pakai-dp' },
    'pencairan-kartu': { base: '/transaksi/pencairan-kartu' },
    'kas-masuk': { base: '/transaksi/kas-masuk' },
    'kas-keluar': { base: '/transaksi/kas-keluar' },
    'mutasi-kas-masuk': { base: '/transaksi/mutasi-kas-masuk' },
    'mutasi-kas-keluar': { base: '/transaksi/mutasi-kas-keluar' },
    'giro-cair-masuk': { base: '/transaksi/giro-cair-masuk' },
    'giro-cair-keluar': { base: '/transaksi/giro-cair-keluar' },
    'giro-tolak-masuk': { base: '/transaksi/giro-tolak-masuk' },
    'giro-tolak-keluar': { base: '/transaksi/giro-tolak-keluar' },
    'giro-ganti-masuk': { base: '/transaksi/giro-ganti-masuk' },
    'giro-ganti-keluar': { base: '/transaksi/giro-ganti-keluar' },
    jurnal: { base: '/transaksi/jurnal' },
    'pembelian-aset': { base: '/transaksi/beli-aset' },
    'penjualan-aset': { base: '/transaksi/jual-aset' },
    'penyesuaian-stock': { base: '/transaksi/penyesuaian-brg' },
    'opname-cetak': { base: '/transaksi/opname-brg' },
    'transfer-kirim': { base: '/transaksi/transfer-brg' },
} as const satisfies Record<string, { base: string }>;

export type JenisVoid = keyof typeof VOID_API;

/** Header token otorisasi void (BE voidNota.service HEADER). */
export const HEADER_OTORISASI_VOID = 'X-Otorisasi-Void';
/** Flag otorisasi BE (otorisasi.service FLAG_VOID). */
export const FLAG_VOID = 'Void';
/** Format(wmUserNoAuth, ['membatalkan']) — Units/MGMessage.pas:57, MGTransForm.pas:1268 */
export const PESAN_TIDAK_BERHAK_VOID = 'Maaf, anda tidak punya otoritas untuk membatalkan .';
/** Kode 403 BE saat void tanpa hak / token otorisasi sah. */
export const KODE_PERLU_OTORISASI_VOID = 'PERLU_OTORISASI_VOID';

/** Path request void nota `id` jenis `jenis`. */
export function pathVoid(jenis: JenisVoid, id: string | number): string {
    return `${VOID_API[jenis].base}/${encodeURIComponent(String(id))}/void`;
}

/** path void → token (sesi halaman; Delphi juga meminta Log In setiap kali Void diklik). */
const registry = new Map<string, string>();

export function simpanTokenVoid(jenis: JenisVoid, id: string | number, token: string): void {
    registry.set(pathVoid(jenis, id), token);
}

export function hapusTokenVoid(jenis: JenisVoid, id: string | number): void {
    registry.delete(pathVoid(jenis, id));
}

/** Normalisasi URL request axios → path relatif `/api` tanpa query / garis miring akhir. */
function normalPath(url: string): string {
    let p = String(url || '');
    const q = p.search(/[?#]/);
    if (q >= 0) p = p.slice(0, q);
    p = p.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/?api(?=\/)/, '');
    if (!p.startsWith('/')) p = `/${p}`;
    return p.replace(/\/+$/, '') || '/';
}

/** Token otorisasi void untuk request (hanya PUT / POST ke path void nota yang terdaftar), atau null. */
export function tokenVoidUntuk(method: string | undefined, url: string | undefined): string | null {
    const m = String(method || '').toLowerCase();
    if ((m !== 'put' && m !== 'post') || !url || registry.size === 0) return null;
    return registry.get(normalPath(url)) ?? null;
}
